import React, { useState } from 'react';
import { Calendar, User, CreditCard, CheckCircle, Clock, AlertTriangle, MoreVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function PaymentTimeline({ negotiation, onStatusUpdate }) {
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'pending':
        return <Clock className="text-blue-500" size={24} />;
      case 'overdue':
        return <AlertTriangle className="text-red-500" size={24} />;
      default:
        return <Clock className="text-gray-500" size={24} />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      paid: 'Confirmado',
      pending: 'Pendente',
      overdue: 'Atrasado',
      partial: 'Parcial'
    };
    return labels?.[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      paid: 'bg-green-500',
      pending: 'bg-blue-500',
      overdue: 'bg-red-500',
      partial: 'bg-yellow-500'
    };
    return colors?.[status] || 'bg-gray-500';
  };

  const handleStatusChange = (receivableId, newStatus) => {
    onStatusUpdate(receivableId, newStatus);
  };

  const payments = negotiation?.receivables || [];
  
  // Pagination calculations
  const totalPages = Math.ceil(payments?.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedPayments = payments?.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setExpandedPayment(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Negotiation Header */}
      <div className="mb-6 pb-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-2">{negotiation?.title}</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Cliente:</span>
            <span className="ml-2 font-medium text-gray-900">{negotiation?.clientName}</span>
          </div>
          <div>
            <span className="text-gray-500">Destino:</span>
            <span className="ml-2 font-medium text-gray-900">{negotiation?.destination || 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-500">Valor Total:</span>
            <span className="ml-2 font-medium text-gray-900">
              R$ {parseFloat(negotiation?.saleValue || 0)?.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Data da Venda:</span>
            <span className="ml-2 font-medium text-gray-900">
              {negotiation?.saleDate && format(new Date(negotiation.saleDate), 'dd/MM/yyyy', { locale: ptBR })}
            </span>
          </div>
        </div>
      </div>
      {/* Pagination Info */}
      {payments?.length > 0 && (
        <div className="mb-4 flex items-center justify-between bg-blue-50 px-4 py-3 rounded-lg">
          <div className="text-sm text-gray-700">
            Exibindo <span className="font-semibold">{startIndex + 1}</span> a{' '}
            <span className="font-semibold">{Math.min(endIndex, payments?.length)}</span> de{' '}
            <span className="font-semibold">{payments?.length}</span> pagamentos
          </div>
          <div className="text-sm text-gray-600">
            Página {currentPage} de {totalPages}
          </div>
        </div>
      )}
      {/* Timeline */}
      <div className="relative">
        {/* Vertical Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

        {/* Payment Items */}
        <div className="space-y-8">
          {payments?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Nenhum pagamento registrado para esta negociação
            </div>
          ) : (
            paginatedPayments?.map((payment, index) => {
              const absoluteIndex = startIndex + index;
              return (
                <div key={payment?.id} className="relative pl-16">
                  {/* Timeline Node */}
                  <div className="absolute left-3 top-0">
                    <div className={`w-6 h-6 rounded-full ${getStatusColor(payment?.status)} flex items-center justify-center`}>
                      <span className="text-white text-xs font-bold">{absoluteIndex + 1}</span>
                    </div>
                  </div>

                  {/* Payment Card */}
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200 hover:border-blue-300 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(payment?.status)}
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            Pagamento {absoluteIndex + 1} - R$ {parseFloat(payment?.amount)?.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </h4>
                          <span className={`inline-block mt-1 px-2 py-1 rounded text-xs font-medium ${
                            payment?.status === 'paid' ? 'bg-green-100 text-green-800' :
                            payment?.status === 'pending'? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {getStatusLabel(payment?.status)}
                          </span>
                        </div>
                      </div>

                      {/* Status Update Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setExpandedPayment(expandedPayment === payment?.id ? null : payment?.id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <MoreVertical size={18} />
                        </button>

                        {expandedPayment === payment?.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                            <button
                              onClick={() => {
                                handleStatusChange(payment?.id, 'paid');
                                setExpandedPayment(null);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                            >
                              Marcar como Pago
                            </button>
                            <button
                              onClick={() => {
                                handleStatusChange(payment?.id, 'pending');
                                setExpandedPayment(null);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                            >
                              Marcar como Pendente
                            </button>
                            <button
                              onClick={() => {
                                handleStatusChange(payment?.id, 'overdue');
                                setExpandedPayment(null);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                            >
                              Marcar como Atrasado
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payment Details Grid */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar size={16} />
                        <span>Vencimento: {format(new Date(payment.dueDate), 'dd/MM/yyyy', { locale: ptBR })}</span>
                      </div>

                      {payment?.receivedAt && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <CheckCircle size={16} />
                          <span>Recebido: {format(new Date(payment.receivedAt), 'dd/MM/yyyy', { locale: ptBR })}</span>
                        </div>
                      )}

                      {payment?.operator && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <User size={16} />
                          <span>Operador: {payment?.operator}</span>
                        </div>
                      )}

                      {payment?.paymentMethod && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <CreditCard size={16} />
                          <span>Método: {payment?.paymentMethod}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Pagination Controls */}
      {payments?.length > ITEMS_PER_PAGE && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center gap-1 px-4 py-2 rounded-lg border transition-colors ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' :'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <ChevronLeft size={18} />
            Anterior
          </button>

          <div className="flex items-center gap-1">
            {[...Array(totalPages)]?.map((_, idx) => {
              const pageNum = idx + 1;
              // Show first 3, last 3, and pages around current page
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg border transition-colors ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white border-blue-600' :'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (
                pageNum === currentPage - 2 ||
                pageNum === currentPage + 2
              ) {
                return (
                  <span key={pageNum} className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }
              return null;
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center gap-1 px-4 py-2 rounded-lg border transition-colors ${
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' :'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Próxima
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}